/**
 * PHARMAKON — Axios adapter for BETA mock backend
 *
 * Axios uses an "adapter" function as the lowest layer that turns a config
 * into an HTTP response (XHR / fetch / node-http). By swapping in this
 * adapter, every existing service call (api.get, api.post, …) routes to
 * our in-memory router instead of the network — zero changes required to
 * any page or store.
 *
 * The adapter:
 *  1. Strips the API base prefix (/api/v1) from the URL.
 *  2. Hands the request to ./router.js#dispatch.
 *  3. Wraps the handler result in the AxiosResponse shape callers expect.
 *  4. On 4xx/5xx, rejects with an AxiosError-compatible object (so the
 *     existing error interceptor + try/catch sites keep working).
 */
import { dispatch } from "./router.js";

/**
 * Strip the API base path so handlers see clean URLs like "/produits/123".
 * Accepts absolute (https://api.foo/api/v1/x) or relative ("/api/v1/x").
 */
function stripBase(url) {
  if (!url) return "/";
  // Absolute URL — drop scheme + host
  let path;
  try {
    if (/^https?:/i.test(url)) {
      const u = new URL(url);
      path = u.pathname + u.search;
    } else {
      path = url;
    }
  } catch {
    path = url;
  }
  // Strip any /api/v\d+ prefix once.
  path = path.replace(/^\/api\/v\d+/, "");
  if (!path.startsWith("/")) path = "/" + path;
  return path;
}

/** Build the AxiosResponse object axios callers expect. */
function buildResponse(config, status, data) {
  return {
    data,
    status,
    statusText: status === 200 ? "OK" : "ERR",
    headers: {},
    config,
    request: { fromBetaMock: true },
  };
}

/** Build an AxiosError-shaped rejection. */
function buildError(config, status, data) {
  const e = new Error(data?.message ?? `Mock error ${status}`);
  e.isAxiosError = true;
  e.config = config;
  e.response = buildResponse(config, status, data);
  return e;
}

/**
 * The actual adapter function. Signature matches axios' adapter contract:
 * (config) => Promise<AxiosResponse>.
 */
export default async function mockAdapter(config) {
  const method = (config.method || "get").toUpperCase();
  // axios merges params into URL with paramsSerializer — we get a clean
  // URL with the query string already appended when `params` was used.
  let url = config.url || "/";
  if (config.baseURL && !/^https?:/i.test(url)) {
    // axios merges baseURL itself in some setups, but our singleton uses
    // request.url as-is — handle both safely.
    if (!url.startsWith(config.baseURL)) url = config.baseURL + url;
  }
  const path = stripBase(url);

  // Inject query params separately so handlers can read them without
  // re-parsing the URL string.
  const queryParams = {};
  if (path.includes("?")) {
    const qs = path.split("?")[1];
    new URLSearchParams(qs).forEach((v, k) => (queryParams[k] = v));
  }
  // Also accept axios `params` passed alongside `url`.
  if (config.params) Object.assign(queryParams, config.params);

  // Parse body. FormData (e.g. logo upload) is passed through opaquely;
  // handlers that care can sniff `body instanceof FormData`.
  let body = config.data;
  if (typeof body === "string") {
    try {
      body = JSON.parse(body);
    } catch {
      /* leave as string */
    }
  }

  // Simulate a small latency so loading spinners get a chance to render —
  // the UX feels more "real" than instant teleports.
  await new Promise((r) => setTimeout(r, 80));

  const { status, data } = await dispatch({
    method,
    url: path,
    body,
    params: queryParams,
  });

  if (status >= 200 && status < 300) {
    return buildResponse(config, status, data);
  }
  return Promise.reject(buildError(config, status, data));
}
